import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getArchivedListNextPage } from '../../actions/archivedList';
import { getChatListNextPage } from '../../actions/chatList';
import { getSpamListNextPage } from '../../actions/spamList';
import { ChatList, ChatListLayout } from '../../components/ChatList';
import { ChatListSupportItem } from '../../components/ChatListSupportItem';
import { EmptyChatList } from '../../components/EmptyChatList';
import { useFeedbackForm } from '../../hooks/useFeedbackForm';
import { chatListSelector, userSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { ERoute } from '../../types/route';
import { AuthNotificationContainer } from '../AuthNotificationContainer';
import { ChatListContextMenuContainer } from '../ChatListContextMenuContainer';
import { ChatListItemContainer } from '../ChatListItemContainer';
import { ListActionButtonsContainer } from '../ListActionButtonsContainer';
import { TopMenuContainer } from '../TopMenuContainer';

export const ChatListContainer: React.VFC = () => {
  const dispatch = useDispatch();
  const { requestFeedbackForm } = useFeedbackForm();

  const { chats, currentRoute, allChatsLoaded, movedToArchiveChatIds, movedToSpamChatIds, status } =
    useSelector(chatListSelector);

  const { isAuthenticated } = useSelector(userSelector);

  const listRef = React.useRef<HTMLDivElement>(null);

  const chatsToDisplay = React.useMemo(() => {
    /**
     * Если находимся на странице активных чатов,
     * заблокированные чаты (в спаме) или архивные чаты
     * или чаты которые только что перенесли в спам или архив - не выводим
     */
    if (currentRoute === 'messages') {
      return chats.filter(
        chat =>
          !chat.blocked &&
          !chat.archived &&
          !movedToSpamChatIds.includes(chat.chatId as string) &&
          !movedToArchiveChatIds.includes(chat.chatId as string),
      );
    }

    return chats;
  }, [currentRoute, chats, movedToSpamChatIds, movedToArchiveChatIds]);

  const getNextPage = React.useCallback(() => {
    if (currentRoute === ERoute.Spam) {
      return dispatch(getSpamListNextPage());
    }

    if (currentRoute === ERoute.Messages) {
      return dispatch(getChatListNextPage());
    }

    if (currentRoute === ERoute.Archive) {
      return dispatch(getArchivedListNextPage());
    }

    return;
  }, [dispatch, currentRoute]);

  return (
    <ChatListLayout
      topMenu={
        <TopMenuContainer isChangeListVisible={Boolean(chatsToDisplay.length && currentRoute !== ERoute.Spam)} />
      }
      chatListContent={
        chatsToDisplay.length ? (
          <ChatList allChatsLoaded={allChatsLoaded} status={status} getNextPage={getNextPage} listRef={listRef}>
            {currentRoute === ERoute.Messages && <ChatListSupportItem onClick={requestFeedbackForm} />}
            {chatsToDisplay.map(chat => (
              <ChatListItemContainer chat={chat} key={`chat-item-${chat.chatId}`} />
            ))}
          </ChatList>
        ) : (
          <>
            {currentRoute === ERoute.Messages && <ChatListSupportItem onClick={requestFeedbackForm} isLast />}
            {!isAuthenticated && (
              <Responsive phone tablet>
                <AuthNotificationContainer />
              </Responsive>
            )}
            {isAuthenticated && status === ERequestApiStatus.Succeed ? <EmptyChatList route={currentRoute} /> : null}
          </>
        )
      }
      listActionButtons={<ListActionButtonsContainer />}
      contextMenu={<ChatListContextMenuContainer listRef={listRef} />}
    />
  );
};
